import { IPolConf, IPolManualConf, LIFE_TEST_ID, POL_TYPE, PolPreviewDevice } from '../../models/life-check';
import { TestStatus } from '../../models/user-identification';
import {
	DEFAULT_FACE_DEPTH_REAL_THRESHOLDS,
	FaceDepthRealThresholds,
} from 'src/app/models/services/face-depth-real.models';

/** Protocolo approach-face (NIF-739): holds, zoom lejos y umbrales de acercamiento. */
export const approachFaceProtocol = {
	farHoldMs: 3000,
	nearHoldMs: 1200,
	farZoom: 1.65,
	minScaleRatio: 1.22,
	minPhaseMs: 1000,
	readyHoldMs: 500,
} as const;

export type ApproachFaceProtocol = typeof approachFaceProtocol;

/**
 * Umbrales lab far↔near. En móvil el proxy de profundidad (bbox+zoom far) infla Δ
 * (~0.39–0.55 observados) y chocaba con max PC 0.345.
 */
export const approachFaceLabThresholdsByDevice: Record<PolPreviewDevice, FaceDepthRealThresholds> = {
	pc: { ...DEFAULT_FACE_DEPTH_REAL_THRESHOLDS },
	mobile: {
		...DEFAULT_FACE_DEPTH_REAL_THRESHOLDS,
		maxDeltaDepthM: 0.55,
	},
};

export function getApproachFaceLabThresholds(device: PolPreviewDevice): FaceDepthRealThresholds {
	return approachFaceLabThresholdsByDevice[device] ?? approachFaceLabThresholdsByDevice.pc;
}

export const lifeCheckConf: IPolConf = {
	middleFace: {
		lifeTestId: LIFE_TEST_ID.MIDDLEFACE,
		status: TestStatus.OnInit,
		complete: false,
		photos: [],
		messages: {
			onInit: 'LIFE_CHECK.MIDDLE_FACE.TEXT.ONINIT',
			onWarning: 'LIFE_CHECK.MIDDLE_FACE.TEXT.ONWARNING',
			onFail: 'LIFE_CHECK.GENERAL.TEXT.ONWARNING',
			onComplete: 'LIFE_CHECK.MIDDLE_FACE.TEXT.ONCOMPLETE',
		},
		minTime: 3,
		maxTime: 50,
		startAt: '',
		finishAt: '',
	},
	lifeTest: [
		{
			lifeTestId: LIFE_TEST_ID.SMILE,
			minPercentageFaceBlendshape: 50,
			minTransitionsFaceBlendshape: 4,
			photos: [],
			status: TestStatus.OnInit,
			complete: false,
			startAt: '',
			finishAt: '',
			minTime: 1.5,
			maxTime: 60,
			messages: {
				onInit: 'LIFE_CHECK.SMILE.TEXT.ONINIT',
				onWarning: 'LIFE_CHECK.SMILE.TEXT.ONWARNING',
				onFail: 'LIFE_CHECK.GENERAL.TEXT.ONWARNING',
				onComplete: 'LIFE_CHECK.SMILE.TEXT.ONCOMPLETE',
			},
		},
		{
			lifeTestId: LIFE_TEST_ID.TURNRIGHTHEAD,
			minPercentageFaceBlendshape: 50,
			minTransitionsFaceBlendshape: 4,
			noseMovement: {
				minDistance: 55,
				minPercentageVariance: 50,
				minTransitions: 4,
			},
			photos: [],
			status: TestStatus.OnInit,
			complete: false,
			startAt: '',
			finishAt: '',
			minTime: 1,
			maxTime: 60,
			messages: {
				onInit: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONINIT',
				onWarning: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONWARNING',
				onFail: 'LIFE_CHECK.GENERAL.TEXT.ONWARNING',
				onComplete: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONCOMPLETE',
			},
		},
		{
			lifeTestId: LIFE_TEST_ID.TURNLEFTHEAD,
			minPercentageFaceBlendshape: 50,
			minTransitionsFaceBlendshape: 4,
			noseMovement: {
				minDistance: 55,
				minPercentageVariance: 50,
				minTransitions: 4,
			},
			photos: [],
			status: TestStatus.OnInit,
			complete: false,
			startAt: '',
			finishAt: '',
			minTime: 1,
			maxTime: 60,
			messages: {
				onInit: 'LIFE_CHECK.TURN_LEFT_HEAD.TEXT.ONINIT',
				onWarning: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONWARNING',
				onFail: 'LIFE_CHECK.GENERAL.TEXT.ONWARNING',
				onComplete: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONCOMPLETE',
			},
		},
		{
			lifeTestId: LIFE_TEST_ID.APPROACHFACE,
			minPercentageFaceBlendshape: 0,
			minTransitionsFaceBlendshape: 0,
			photos: [],
			status: TestStatus.OnInit,
			complete: false,
			startAt: '',
			finishAt: '',
			minTime: 3,
			maxTime: 60,
			messages: {
				onInit: 'LIFE_CHECK.GENERAL.TEXT.IS_TOO_CLOSE_1',
				onWarning: 'LIFE_CHECK.GENERAL.TEXT.IS_TOO_FAR_1',
				onFail: 'LIFE_CHECK.GENERAL.TEXT.ONWARNING',
				onComplete: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONCOMPLETE',
			},
		},
		/* {
			lifeTestId: LIFE_TEST_ID.LIFTHEAD,
			minPercentageFaceBlendshape: 40,
			minTransitionsFaceBlendshape: 3,
			noseMovement: {
				minDistance: 60,
				minPercentageVariance: 60,
				minTransitions: 5,
			},
			photos: [],
			status: TestStatus.OnInit,
			complete: false,
			startAt: '',
			finishAt: '',
			minTime: 1.5,
			maxTime: 60,
			messages: {
				onInit: 'LIFE_CHECK.LIFT_HEAD.TEXT.ONINIT',
				onWarning: 'LIFE_CHECK.LIFT_HEAD.TEXT.ONWARNING',
				onFail: 'LIFE_CHECK.GENERAL.TEXT.ONWARNING',
				onComplete: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONCOMPLETE',
				// Se usa el mismo audio
				// onFail: 'LIFE_CHECK.LIFT_HEAD.TEXT.ONFAIL',
				// onComplete: 'LIFE_CHECK.LIFT_HEAD.TEXT.ONCOMPLETE',
			},
		}, */
		/* {
			lifeTestId: LIFE_TEST_ID.LOWERHEAD,
			minPercentageFaceBlendshape: 40,
			minTransitionsFaceBlendshape: 3,
			noseMovement: {
				minDistance: 60,
				minPercentageVariance: 60,
				minTransitions: 5,
			},
			photos: [],
			status: TestStatus.OnInit,
			complete: false,
			startAt: '',
			finishAt: '',
			minTime: 2,
			maxTime: 60,
			messages: {
				onInit: 'LIFE_CHECK.LOWER_HEAD.TEXT.ONINIT',
				onWarning: 'LIFE_CHECK.LOWER_HEAD.TEXT.ONWARNING',
				onFail: 'LIFE_CHECK.MIDDLE_FACE.TEXT.ONFAIL',
				onComplete: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONCOMPLETE',
				// Se usa el mismo audio
				// onFail: 'LIFE_CHECK.LOWER_HEAD.TEXT.ONFAIL',
				// onComplete: 'LIFE_CHECK.LOWER_HEAD.TEXT.ONCOMPLETE',
			},
		},*/
	],
	general: {
		startAt: '',
		finishAt: '',
		minPercenteageTimeInMiddle: 75,
		minPercenteageBlink: 60,
		minEnvironmentSimilarityPercenteage: 50,
		minFaceSimilarityPercenteage: 40,
		minTotalSimilarityPercenteage: 50,
		timeBetweenTest: 5,
		type: POL_TYPE.RANDOM,
		totalLifeTest: 2,
		controlValidation: false,
		messages: {
			onInit: 'LIFE_CHECK.GENERAL.TEXT.ONINIT',
			onWarning: 'LIFE_CHECK.GENERAL.TEXT.ONWARNING',
			onFail: 'LIFE_CHECK.GENERAL.TEXT.ONFAIL',
			onComplete: 'LIFE_CHECK.GENERAL.TEXT.ONCOMPLETE',
			onWarningMismatch: 'LIFE_CHECK.GENERAL.TEXT.MISMATCH_WARNING',
		},
		countTries: 1,
		tries: 3,
		complete: false,
		minScoring: 7,
		status: TestStatus.OnInit,
		observations: [],
		operationId: '',
	},
};

/**
 * Zoom lógico adicional sobre el recorte `object-fit: cover` (1 = solo alinear preview/análisis).
 * Preview CSS y canvas de análisis usan el valor del dispositivo activo (`PolPreviewDevice` en models).
 * El JPEG de `/liveness` en PC usa zoom-out de payload + canvas completo (NIF-951).
 */
export const polAnalysisExtraZoomFactorByDevice: Record<PolPreviewDevice, number> = {
	pc: 1,
	mobile: 1.55,
};

export function getPolAnalysisExtraZoomFactor(device: PolPreviewDevice): number {
	return polAnalysisExtraZoomFactorByDevice[device];
}

/** Aspecto del JPEG enviado a `/liveness` en móvil (mismo que el óvalo 336×528). */
export const POL_LIVENESS_JPEG_ASPECT = 336 / 528;

/**
 * Zoom del recorte cover al generar fotos de `/liveness` (independiente del preview).
 * 0.7 en PC y móvil: más sensor alrededor de la cara (el preview 1.55 no basta solo).
 */
export const polPayloadZoomFactorByDevice: Record<PolPreviewDevice, number> = {
	pc: 0.7,
	mobile: 0.7,
};

export function getPolPayloadZoomFactor(device: PolPreviewDevice): number {
	return polPayloadZoomFactorByDevice[device];
}

/**
 * Zoom del JPEG en POL estática (`manual-life-check`), independiente del preview.
 * 0.7 en PC y móvil: el preview no lleva zoomFactor; el payload aporta contexto.
 */
export const polStaticPayloadZoomFactorByDevice: Record<PolPreviewDevice, number> = {
	pc: 0.7,
	mobile: 0.7,
};

export function getPolStaticPayloadZoomFactor(device: PolPreviewDevice): number {
	return polStaticPayloadZoomFactorByDevice[device];
}

/**
 * En POL estática el JPEG es siempre el canvas cover+zoom (sin tira 336/528).
 * En móvil 16:9 la tira volvía a dejar el mismo encuadre que antes del zoom-out.
 */
export const polStaticPayloadUseFullCanvasByDevice: Record<PolPreviewDevice, boolean> = {
	pc: true,
	mobile: true,
};

export function getPolStaticPayloadUseFullCanvas(device: PolPreviewDevice): boolean {
	return polStaticPayloadUseFullCanvasByDevice[device];
}

/**
 * Tamaño CSS del `<video>` en PC (más ancho que 355×560 del óvalo) para que el cover
 * de 16:9 recorte menos laterales. El óvalo de `tec-scan-face` sigue centrado encima.
 */
export const POL_PC_PREVIEW_VIDEO_PX = { width: 640, height: 560 } as const;

/** Canvas completo tras cover+zoom: la tira 336/528 anulaba el zoom-out (PC y móvil). */
export const polPayloadUseFullCanvasByDevice: Record<PolPreviewDevice, boolean> = {
	pc: true,
	mobile: true,
};

export function getPolPayloadUseFullCanvas(device: PolPreviewDevice): boolean {
	return polPayloadUseFullCanvasByDevice[device];
}

export interface IPolLivenessJpegCrop {
	srcX: number;
	srcY: number;
	srcWidth: number;
	srcHeight: number;
}

/**
 * Tira 336/528 sobre el canvas de análisis (solo móvil). Recorte centrado en X y desde arriba.
 */
export function getPolLivenessJpegCrop(canvasWidth: number, canvasHeight: number): IPolLivenessJpegCrop {
	if (canvasWidth <= 0 || canvasHeight <= 0) {
		return { srcX: 0, srcY: 0, srcWidth: 0, srcHeight: 0 };
	}

	let cropHeight = canvasHeight;
	let cropWidth = cropHeight * POL_LIVENESS_JPEG_ASPECT;

	if (canvasWidth < canvasHeight) {
		cropWidth = canvasWidth;
		cropHeight = cropWidth / POL_LIVENESS_JPEG_ASPECT;
	}

	const srcWidth = Math.min(Math.round(cropWidth), canvasWidth);
	const srcHeight = Math.min(Math.round(cropHeight), canvasHeight);
	const srcX = Math.round((canvasWidth - srcWidth) / 2);

	return { srcX, srcY: 0, srcWidth, srcHeight };
}

export const manualLifeCheckConf: IPolManualConf = {
	general: {
		startAt: '',
		finishAt: '',
		maxTime: 300,
		photo: null,
		observations: [],
		status: TestStatus.OnInit,
	},
};
